import {
  type ClaimCitation,
  type CompareRoute,
  checkFindings,
  FINDINGS_LIMITS,
  FINDINGS_STATUS_LABELS,
  type Finding,
  type FindingsSection,
  findingsCaveats,
  otherLanguage,
  REPORT_LANGUAGE_LABELS,
  REPORT_LANGUAGE_NAMES,
  type ReportLanguage,
  ROUTE_LABELS,
} from '@hoopoe/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, BadgeCheck, Eye, Languages, Lock, MessageSquareText, Pencil, Save, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { DataNumber, DataText } from '../components/data/data-text';
import { DraftedBy } from '../components/findings/drafted-by';
import {
  citationNumbers,
  FindingsCaveats,
  FindingsSources,
  SectionEditor,
  SectionText,
  SectionView,
} from '../components/findings/findings-text';
import { RedraftPanel } from '../components/findings/redraft-panel';
import { SectionProgress } from '../components/findings/section-progress';
import { TranslationView } from '../components/findings/translation-view';
import { ConnectionLost } from '../components/guidance/connection-lost';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { EditorError } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  adoptFindingsDraft,
  checkFindingsWork,
  deleteFindings,
  draftWithAi,
  FindingsLostError,
  type FindingsWork,
  findingsKey,
  findingsLostMessage,
  nextSectionProgress,
  type RedraftRequest,
  type SectionProgressItem,
  saveFindings,
  signOffFindings,
  translateWithAi,
  useFinding,
} from '../lib/findings';

// AI findings (PLAN §10.2, §12.7 screen 8; task 32): drafted by AI from a saved report or a Case, every sentence citing
// the claims it rests on; read with numbered source chips and the caveats a reader must know; edited as a small rich
// text with a Cite button; signed off by the author, a reviewer or an admin. In Compare mode the drafts are shown side
// by side and one is kept. A templated report drafts section by section: the progress shows each section, and an
// executive summary (an ordinary section) opens the findings. Findings are written in their report's language and can
// be translated into the other one; the translation is shown read-only beside the original, marked when out of date.

export const Route = createFileRoute('/findings/$id')({ component: FindingsPage });

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
const dateTime = (ms: number) =>
  new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function FindingsPage() {
  const { id } = Route.useParams();
  const q = useFinding(id);
  if (q.isError)
    return (
      <>
        <PageHeader title="Findings" actions={<Back />} />
        <ErrorState error={q.error} title="These findings couldn’t be opened" onRetry={() => q.refetch()} learnMore="draft-ai-findings" />
      </>
    );
  if (!q.data)
    return (
      <div aria-busy="true" className="space-y-3">
        <span className="sr-only">Loading…</span>
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-60 animate-pulse rounded-md bg-muted" />
      </div>
    );
  return <Editor key={q.data.id} finding={q.data} />;
}

function Back({ finding }: { finding?: Finding }) {
  return (
    <Button size="sm" variant="ghost" asChild>
      {finding?.source.kind === 'report' ? (
        <Link to="/reports/$id" params={{ id: finding.source.id }}>
          <ArrowLeft aria-hidden="true" /> Back to the report
        </Link>
      ) : (
        <Link to="/reports">
          <ArrowLeft aria-hidden="true" /> All reports
        </Link>
      )}
    </Button>
  );
}

function StatusLine({ f }: { f: Finding }) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground" data-testid="findings-status">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
          f.status === 'signed_off' ? 'border-success text-success-foreground' : 'text-foreground',
        )}
      >
        {f.status === 'signed_off' && <BadgeCheck className="size-3.5" aria-hidden="true" />}
        {FINDINGS_STATUS_LABELS[f.status]}
      </span>
      {f.signedOff && (
        <span>
          Signed off by <DataText>{f.signedOff.name ?? 'someone'}</DataText> on <DataNumber>{dateTime(f.signedOff.at)}</DataNumber>
        </span>
      )}
      {f.model && (
        <span>
          <DraftedBy route={f.route} model={f.model} sections={f.sections} />
          {f.draftedAt ? (
            <>
              {' '}
              on <DataNumber>{dateTime(f.draftedAt)}</DataNumber>
            </>
          ) : null}
          {f.draftNotes ? ', steered by your notes' : null}
        </span>
      )}
      <span data-testid="findings-language">Written in {REPORT_LANGUAGE_NAMES[f.language]}</span>
      {f.sensitivity === 'confidential' && (
        <span className="inline-flex items-center gap-1">
          <Lock className="size-3.5" aria-hidden="true" /> Confidential material: drafted on Cloudflare only
        </span>
      )}
      <span>
        Started by <DataText>{f.ownerName ?? 'someone'}</DataText>
      </span>
    </p>
  );
}

function Editor({ finding }: { finding: Finding }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [f, setF] = useState(finding);
  const [title, setTitle] = useState(finding.title);
  const [sections, setSections] = useState<FindingsSection[]>(finding.sections);
  const [editing, setEditing] = useState(finding.canEdit && finding.status !== 'signed_off' && finding.status !== 'choose');
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [sectionProgress, setSectionProgress] = useState<SectionProgressItem[]>([]);
  const [progressMode, setProgressMode] = useState<'draft' | 'translate'>('draft');
  /** The language shown: the findings as written, or one of their translations (read-only). */
  const [view, setView] = useState<'original' | ReportLanguage>('original');
  const [redraftOpen, setRedraftOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [lost, setLost] = useState<FindingsWork | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const titleId = useId();
  const anchor = f.id;

  const known = useMemo(() => new Map<string, ClaimCitation>(f.citations.map(c => [c.ref, c])), [f.citations]);
  const numbers = useMemo(() => citationNumbers(sections, known), [sections, known]);
  const check = useMemo(() => checkFindings(sections, new Set(known.keys())), [sections, known]);
  const caveats = useMemo(() => findingsCaveats({ ...f, sections }, check), [f, sections, check]);
  const dirty = title !== f.title || JSON.stringify(sections) !== JSON.stringify(f.sections);
  const shownTranslation = view === 'original' ? null : (f.translations.find(t => t.language === view) ?? null);
  const target = otherLanguage(f.language);
  const targetTranslation = f.translations.find(t => t.language === target) ?? null;
  const canTranslate = f.canEdit && (f.status === 'draft' || f.status === 'signed_off') && f.sections.some(s => s.body.trim());

  // Leaving with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const apply = (next: Finding) => {
    setF(next);
    setTitle(next.title);
    setSections(next.sections);
    qc.setQueryData([...findingsKey, next.id], next);
    qc.invalidateQueries({ queryKey: [...findingsKey, 'list'] });
  };
  const fail = (e: unknown) => {
    // A draft or translation whose stream was lost may still be finishing: shown with Check again (D413).
    if (e instanceof FindingsLostError) return setLost(e.work);
    setError(errorText(e));
    setConflict(e instanceof ApiError && e.code === 'conflict' && typeof e.data.revision === 'number');
  };
  /** Check again: the findings as saved, shown as if the stream had ended with `done` when the work finished. */
  const checkLost = async () => {
    const next = lost && (await checkFindingsWork(lost));
    if (!lost || !next) return false;
    apply(next);
    setLost(null);
    setSectionProgress([]);
    if (lost.kind === 'translate') {
      setView(lost.to);
      setNotice(
        `Translated into ${REPORT_LANGUAGE_NAMES[lost.to]}. The translation is read-only: edit the original, then translate again.`,
      );
    } else {
      setEditing(next.status === 'draft');
      setNotice(
        next.status === 'choose' ? 'The drafts are ready: keep the better one.' : 'Drafted. Read it, fix what needs fixing, then sign off.',
      );
    }
    return true;
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    setNotice(null);
    try {
      apply(await saveFindings(f.id, { revision: f.revision, title, sections }));
      setNotice(f.status === 'signed_off' ? 'Saved. The sign-off was withdrawn: sign off again when ready.' : 'Saved.');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const draft = async (req: RedraftRequest & { count?: number } = {}) => {
    const { count, ...request } = req;
    if (f.status !== 'empty') {
      const some = request.only?.length;
      const ok = await confirm({
        title: some ? `Redraft ${count} ${count === 1 ? 'section' : 'sections'}?` : 'Redraft with AI?',
        body: some
          ? 'A new AI draft replaces the text of the ticked sections, including your edits there, and withdraws any sign-off. The other sections keep their text.'
          : 'A new AI draft replaces the text of every section, including your edits, and withdraws any sign-off. Nothing else changes.',
        confirmLabel: 'Replace with a new draft',
        destructive: true,
      });
      if (!ok) return;
    }
    setRedraftOpen(false);
    setBusy('draft');
    setError(null);
    setLost(null);
    setNotice(null);
    setProgress('Starting…');
    setSectionProgress([]);
    setProgressMode('draft');
    try {
      let done: Finding | null = null;
      let failed: string | null = null;
      let missed: { title: string }[] = [];
      await draftWithAi(
        f.id,
        f.revision,
        e => {
          if (e.type === 'status') setProgress(e.text);
          if (e.type === 'section')
            setSectionProgress(items =>
              nextSectionProgress(
                items.length ? items : sections.map(s => ({ key: s.key, title: s.title, state: 'waiting' as const, message: null })),
                e,
              ),
            );
          if (e.type === 'routes')
            setProgress(
              `Read ${e.claims} ${e.claims === 1 ? 'claim' : 'claims'} of ${e.entities} ${e.entities === 1 ? 'entity' : 'entities'}. ${
                e.compare ? `Drafting with ${e.routes.map(r => ROUTE_LABELS[r]).join(', ')}…` : 'Drafting…'
              }${e.sensitivity === 'confidential' ? ' Confidential material: a Cloudflare-native model writes it.' : ''}`,
            );
          if (e.type === 'done') {
            done = e.finding;
            missed = e.failed ?? [];
          }
          if (e.type === 'error') failed = e.message;
        },
        undefined,
        request,
      );
      if (done) {
        const next = done as Finding;
        apply(next);
        setEditing(next.status === 'draft');
        setNotice(
          next.status === 'choose'
            ? 'The drafts are ready: keep the better one.'
            : missed.length
              ? `Drafted, except ${missed.map(m => `“${m.title}”`).join(', ')}: ${missed.length === 1 ? 'its text is' : 'their text is'} empty. Write ${missed.length === 1 ? 'it' : 'them'}, or redraft later.`
              : 'Drafted. Read it, fix what needs fixing, then sign off.',
        );
        // Keep the list only when a section failed, so the reason stays visible.
        if (!missed.length) setSectionProgress([]);
      } else setError(failed ?? 'The draft couldn’t be written. Try again.');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  /** Translates the findings as saved into the other language, then shows the translation. */
  const translate = async () => {
    setBusy('translate');
    setError(null);
    setLost(null);
    setNotice(null);
    setProgress('Starting…');
    setSectionProgress([]);
    setProgressMode('translate');
    try {
      let done: Finding | null = null;
      let failed: string | null = null;
      await translateWithAi(f.id, f.revision, target, e => {
        if (e.type === 'status') setProgress(e.text);
        if (e.type === 'section')
          setSectionProgress(items =>
            nextSectionProgress(
              items.length ? items : f.sections.map(s => ({ key: s.key, title: s.title, state: 'waiting' as const, message: null })),
              e,
            ),
          );
        if (e.type === 'done') done = e.finding;
        if (e.type === 'error') failed = e.message;
      });
      if (done) {
        apply(done);
        setView(target);
        setSectionProgress([]);
        setNotice(
          `Translated into ${REPORT_LANGUAGE_NAMES[target]}. The translation is read-only: edit the original, then translate again.`,
        );
      } else setError(failed ?? 'The translation couldn’t be made. Try again.');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const keep = async (route: CompareRoute) => {
    setBusy('adopt');
    setError(null);
    try {
      apply(await adoptFindingsDraft(f.id, route));
      setEditing(true);
      setNotice(`Kept ${ROUTE_LABELS[route]}’s draft.`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const signOff = async () => {
    let acknowledge = false;
    if (check.uncited.length) {
      const n = check.uncited.length;
      acknowledge = await confirm({
        title: `Sign off with ${n} unsourced ${n === 1 ? 'sentence' : 'sentences'}?`,
        body: (
          <>
            <p>These cite no claim:</p>
            <ul className="mt-2 list-disc space-y-1 ps-5">
              {check.uncited.slice(0, 5).map((u, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list shown once in a dialog
                <li key={i}>
                  <DataText>{u.text}</DataText> <span className="text-muted-foreground">({u.section})</span>
                </li>
              ))}
            </ul>
            <p className="mt-2">Readers will see them marked “No source”. You can also cancel and add sources first.</p>
          </>
        ),
        confirmLabel: 'Sign off anyway',
      });
      if (!acknowledge) return;
    }
    setBusy('signoff');
    setError(null);
    try {
      apply(await signOffFindings(f.id, f.revision, acknowledge));
      setEditing(false);
      setNotice('Signed off.');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete “${f.title}”?`,
      body: 'The findings text is removed for everyone. The report, the Case and the claims it cites are not touched.',
      confirmLabel: 'Delete findings',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteFindings(f.id);
      await qc.invalidateQueries({ queryKey: [...findingsKey, 'list'] });
      if (f.source.kind === 'report') navigate({ to: '/reports/$id', params: { id: f.source.id } });
      else navigate({ to: '/reports' });
    } catch (e) {
      fail(e);
    }
  };

  const sourceLink =
    f.source.kind === 'report' ? (
      <Link to="/reports/$id" params={{ id: f.source.id }} className="underline underline-offset-2">
        <DataText>{f.source.label}</DataText>
      </Link>
    ) : (
      <Link to="/entity/$id" params={{ id: f.source.id }} className="underline underline-offset-2">
        <DataText>{f.source.label}</DataText>
      </Link>
    );

  return (
    <>
      <PageHeader
        title={f.title}
        description={f.source.kind === 'report' ? 'AI findings drawn from a saved report.' : 'AI findings drawn from a Case.'}
        actions={<Back finding={f} />}
      />
      <div className="space-y-4">
        <p className="text-sm">
          {f.source.kind === 'report' ? 'Report: ' : 'Case: '}
          {sourceLink}
        </p>
        <StatusLine f={f} />
        {f.status === 'empty' && (
          <Tip id="findings-empty" learnMore="draft-ai-findings">
            Draft with AI to get a first version in which every sentence cites the claims it rests on, or write the sections yourself.
            Off-record claims are never used.
          </Tip>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {f.canEdit && f.status !== 'choose' && (
            <Button
              // A new element when the variant changes: no color transition for a checker (or a reader) to catch halfway.
              key={f.status === 'empty' ? 'draft' : 'redraft'}
              onClick={() => draft()}
              disabled={!!busy}
              variant={f.status === 'empty' ? 'primary' : 'outline'}
            >
              <Sparkles aria-hidden="true" /> {busy === 'draft' ? 'Drafting…' : f.status === 'empty' ? 'Draft with AI' : 'Redraft with AI'}
            </Button>
          )}
          {f.canEdit && (f.status === 'draft' || f.status === 'signed_off') && (
            <Button variant="outline" onClick={() => setRedraftOpen(o => !o)} disabled={!!busy} aria-expanded={redraftOpen}>
              <MessageSquareText aria-hidden="true" /> Redraft with notes…
            </Button>
          )}
          {f.canEdit && f.status !== 'choose' && (
            <Button
              variant="ghost"
              aria-pressed={editing && !shownTranslation}
              onClick={() => {
                if (shownTranslation) {
                  setView('original');
                  setEditing(true);
                } else setEditing(e => !e);
              }}
              disabled={!!busy}
            >
              {editing && !shownTranslation ? <Eye aria-hidden="true" /> : <Pencil aria-hidden="true" />}{' '}
              {editing && !shownTranslation ? 'Read view' : 'Edit'}
            </Button>
          )}
          {f.canEdit && dirty && (
            <Button onClick={save} disabled={!!busy}>
              <Save aria-hidden="true" /> {busy === 'save' ? 'Saving…' : 'Save'}
            </Button>
          )}
          {f.canSignOff && f.status === 'draft' && (
            <Button
              key={dirty ? 'signoff-later' : 'signoff'}
              variant={dirty ? 'outline' : 'primary'}
              onClick={signOff}
              disabled={!!busy || dirty || !check.sentences}
              title={dirty ? 'Save your changes before signing off' : undefined}
            >
              <BadgeCheck aria-hidden="true" /> {busy === 'signoff' ? 'Signing off…' : 'Sign off'}
            </Button>
          )}
          {canTranslate && (!targetTranslation || targetTranslation.stale) && (
            <Button
              variant="outline"
              onClick={translate}
              disabled={!!busy || dirty}
              title={dirty ? 'Save your changes before translating' : undefined}
            >
              <Languages aria-hidden="true" />{' '}
              {busy === 'translate'
                ? 'Translating…'
                : targetTranslation
                  ? 'Translate again'
                  : `Translate to ${REPORT_LANGUAGE_NAMES[target]}`}
            </Button>
          )}
          {f.canEdit && (
            <Button variant="ghost" onClick={remove} disabled={!!busy}>
              <Trash2 aria-hidden="true" /> Delete
            </Button>
          )}
          <LearnMore slug="draft-ai-findings" />
        </div>
        {progress && (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="findings-progress">
            <span className="size-3 animate-pulse rounded-full bg-muted-foreground" aria-hidden="true" />
            <DataText>{progress}</DataText>
          </p>
        )}
        {redraftOpen && (
          <RedraftPanel
            sections={f.sections}
            previous={f.draftNotes}
            busy={busy === 'draft'}
            onRedraft={req => draft(req)}
            onCancel={() => setRedraftOpen(false)}
          />
        )}
        <SectionProgress items={sectionProgress} live={busy === 'draft' || busy === 'translate'} mode={progressMode} />
        {f.translations.length > 0 && f.status !== 'choose' && (
          <fieldset className="flex flex-wrap items-center gap-1" data-testid="findings-language-switch">
            <legend className="float-start me-1 text-sm text-muted-foreground">Show</legend>
            <Button
              size="sm"
              variant={view === 'original' ? 'outline' : 'ghost'}
              aria-pressed={view === 'original'}
              onClick={() => setView('original')}
            >
              {REPORT_LANGUAGE_LABELS[f.language]} · original
            </Button>
            {f.translations.map(t => (
              <Button
                key={t.language}
                size="sm"
                variant={view === t.language ? 'outline' : 'ghost'}
                aria-pressed={view === t.language}
                onClick={() => setView(t.language)}
              >
                {REPORT_LANGUAGE_LABELS[t.language]} · translation{t.stale ? ' (out of date)' : ''}
              </Button>
            ))}
          </fieldset>
        )}
        <EditorError error={error} />
        {lost && <ConnectionLost message={findingsLostMessage(lost)} onCheck={checkLost} learnMore="draft-ai-findings" />}
        {conflict && (
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                apply(await api<Finding>(`/findings/${encodeURIComponent(f.id)}`));
                setError(null);
                setConflict(false);
              } catch (e) {
                fail(e);
              }
            }}
          >
            Reload the latest version
          </Button>
        )}
        {notice && (
          <p role="status" className="text-sm text-muted-foreground">
            {notice}
          </p>
        )}

        {f.status === 'choose' ? (
          <CompareDrafts f={f} known={known} busy={!!busy} onKeep={keep} />
        ) : shownTranslation ? (
          <TranslationView
            t={shownTranslation}
            known={known}
            anchor={anchor}
            canTranslate={canTranslate && shownTranslation.language === target}
            busy={!!busy}
            dirty={dirty}
            onTranslate={translate}
          />
        ) : (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <article aria-label="Findings" className="min-w-0 space-y-5 rounded-lg border bg-background p-4">
              {editing && f.canEdit ? (
                <>
                  <div className="space-y-1">
                    <label htmlFor={titleId} className="text-xs font-medium">
                      Title
                    </label>
                    <input
                      id={titleId}
                      dir="auto"
                      className="h-9 w-full rounded-md border border-input bg-surface px-2 text-base font-semibold"
                      value={title}
                      maxLength={FINDINGS_LIMITS.title}
                      onChange={e => setTitle(e.target.value)}
                    />
                  </div>
                  {sections.map((s, i) => (
                    <section key={s.key} aria-label={`Section ${i + 1}: ${s.title}`} className="space-y-3 border-t pt-4">
                      <SectionEditor
                        section={s}
                        citations={f.citations}
                        onChange={next => setSections(all => all.map(x => (x.key === next.key ? next : x)))}
                      />
                      <section className="rounded-md bg-muted p-3" aria-label={`Preview of ${s.title}`}>
                        <p className="mb-1 text-xs font-medium text-muted-foreground">Preview</p>
                        <SectionText body={s.body} numbers={numbers} known={known} anchor={anchor} />
                      </section>
                    </section>
                  ))}
                </>
              ) : (
                sections.map((s, i) => (
                  <section key={s.key} className="space-y-2">
                    <SectionView section={s} numbers={numbers} known={known} anchor={anchor} index={i} />
                  </section>
                ))
              )}
            </article>
            <aside className="space-y-4" aria-label="About these findings">
              <FindingsCaveats caveats={caveats} />
              <section
                aria-label="Citation check"
                className="space-y-1 rounded-lg border bg-background p-4 text-sm"
                data-testid="findings-check"
              >
                <h2 className="text-base font-semibold">Citation check</h2>
                <p>
                  <DataNumber>{check.sentences}</DataNumber> {check.sentences === 1 ? 'sentence' : 'sentences'} ·{' '}
                  <DataNumber>{check.cited.length}</DataNumber> {check.cited.length === 1 ? 'claim' : 'claims'} cited ·{' '}
                  <span className={cn(check.uncited.length > 0 && 'text-warning-foreground')}>
                    <DataNumber>{check.uncited.length}</DataNumber> without a source
                  </span>
                </p>
                {check.unknownRefs.length > 0 && (
                  <p className="text-warning-foreground">
                    Unknown {check.unknownRefs.length === 1 ? 'marker' : 'markers'}: <DataText>{check.unknownRefs.join(', ')}</DataText>.
                    Use Cite to pick a claim.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {f.citations.length} {f.citations.length === 1 ? 'claim' : 'claims'} can be cited: the ones the draft was written from.
                </p>
              </section>
            </aside>
          </div>
        )}
        {f.status !== 'choose' && (
          <FindingsSources
            numbers={shownTranslation ? citationNumbers(shownTranslation.sections, known) : numbers}
            known={known}
            anchor={anchor}
          />
        )}
      </div>
    </>
  );
}

function CompareDrafts({
  f,
  known,
  busy,
  onKeep,
}: {
  f: Finding;
  known: ReadonlyMap<string, ClaimCitation>;
  busy: boolean;
  onKeep: (route: CompareRoute) => void;
}) {
  const drafts = f.drafts ?? [];
  return (
    <section aria-label="Drafts to compare" className="space-y-3">
      <p className="text-sm">
        Compare mode: {drafts.length} AI routes drafted these findings. Read them side by side and keep the better one; you can edit it
        afterwards. <LearnMore slug="draft-ai-findings" section="compare-mode" />
      </p>
      <div className={cn('grid gap-4', drafts.length === 3 ? 'xl:grid-cols-3' : 'md:grid-cols-2')}>
        {drafts.map(d => {
          const sections = f.sections.map(s => ({ ...s, body: d.sections.find(x => x.key === s.key)?.body ?? '' }));
          const numbers = citationNumbers(sections, known);
          const check = checkFindings(sections, new Set(known.keys()));
          return (
            <article key={d.route} aria-label={`${d.label}’s draft`} className="space-y-3 rounded-lg border bg-surface p-3">
              <h2 className="font-semibold">{d.label}</h2>
              <p className="text-xs text-muted-foreground">
                {d.model && <DataText className="font-mono">{d.model}</DataText>}
                {d.ok && ` · ${check.cited.length} claims cited · ${check.uncited.length} without a source`}
              </p>
              {d.ok ? (
                sections.map((s, i) => (
                  <section key={s.key} className="space-y-1">
                    <h3 className="text-sm font-semibold">
                      {i + 1}. <DataText>{s.title}</DataText>
                    </h3>
                    <SectionText body={s.body} numbers={numbers} known={known} anchor={`${f.id}-${d.route}`} />
                  </section>
                ))
              ) : (
                <p role="alert" className="text-sm text-danger-foreground">
                  {d.error ?? 'This route gave no draft.'}
                </p>
              )}
              {d.ok && f.canEdit && (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => onKeep(d.route)}>
                  Keep this draft
                </Button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
